import * as React from 'react';
import { cn } from '@/lib/utils';
import { useScrollReveal } from '@/lib/hooks/use-scroll-reveal';

type Tag =
  | 'div'
  | 'section'
  | 'article'
  | 'header'
  | 'footer'
  | 'aside'
  | 'span'
  | 'p'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'ul'
  | 'li'
  | 'blockquote';

interface ScrollRevealProps extends React.HTMLAttributes<HTMLElement> {
  children: React.ReactNode;
  /** Delay en ms parmi 0/100/200/300/400. Stagger entre éléments d'une même section. */
  delay?: 0 | 100 | 200 | 300 | 400;
  /** Balise wrapper. Default 'div'. */
  as?: Tag;
  className?: string;
}

const delayMap: Record<number, string> = {
  0: '',
  100: 'delay-100',
  200: 'delay-200',
  300: 'delay-300',
  400: 'delay-400'
};

/**
 * ScrollReveal — heavy cinematic fade-up + blur entry at scroll (fires once).
 *
 * Phase 26 Awwwards-tier polish :
 * - translate-y-12 + blur-md → translate-y-0 + blur-0 over 800ms cubic-bezier(0.16,1,0.3,1)
 * - Respecte prefers-reduced-motion via `motion-safe:` (skipped en reduced-motion).
 * - Stagger : `delay` ∈ {0, 100, 200, 300, 400} ms.
 *
 * Usage :
 *   <ScrollReveal delay={100}><h2>Titre</h2></ScrollReveal>
 */
export function ScrollReveal({
  children,
  delay = 0,
  as = 'div',
  className,
  ...rest
}: ScrollRevealProps) {
  const { ref, visible } = useScrollReveal<HTMLElement>();
  const Tag = as as React.ElementType;
  return (
    <Tag
      ref={ref as React.Ref<HTMLElement>}
      className={cn(
        'transition-[opacity,transform,filter] duration-[800ms] [transition-timing-function:cubic-bezier(0.16,1,0.3,1)]',
        visible
          ? 'motion-safe:opacity-100 motion-safe:translate-y-0 motion-safe:blur-0'
          : 'motion-safe:opacity-0 motion-safe:translate-y-12 motion-safe:blur-md',
        delayMap[delay],
        className
      )}
      {...rest}
    >
      {children}
    </Tag>
  );
}
